import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { getId } from '@fluentui/react';
import {
  Combobox,
  Option,
  Field,
  FluentProvider,
  Spinner,
  webLightTheme,
} from '@fluentui/react-components';
import LoadingErrorMessage from '../common/errorContent/LoadingErrorMessage';
import GET_REPORTING_GROUPS_QUERY from '../../utils/statApi/ReportingGroupApi';

interface IComboBoxOption {
  key: string | number;
  text: string;
}

interface IReportingGroupSelectorProps {
  label: string;
  disabled: boolean;
  onChange: (value: IComboBoxOption) => void;
  required: boolean;
  errorMessage?: string;
  defaultValue?: IComboBoxOption;
}

const ReportingGroupSelector = ({
  label,
  disabled,
  onChange,
  required,
  errorMessage,
  defaultValue,
}: IReportingGroupSelectorProps): JSX.Element => {
  const [inputValue, setInputValue] = useState(defaultValue?.text || '');
  const [options, setOptions] = useState([]);

  const { loading, error } = useQuery(GET_REPORTING_GROUPS_QUERY, {
    fetchPolicy: 'cache-and-network',
    onCompleted: (data) => {
      setOptions(data.reportingGroups);
      if (defaultValue) {
        setInputValue(defaultValue.text);
      }
    },
  });

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const { value } = event.target;
    setInputValue(value);
    if (!value.trim()) {
      onChange({ key: '', text: '' });
    }
  };

  const handleOptionSelect = (
    event: React.SyntheticEvent,
    data: { optionValue?: string | number; optionText?: string },
  ): void => {
    if (data.optionValue) {
      const selectedOption = options.find(
        (opt) => opt.key.toString() === data.optionValue.toString(),
      );
      if (selectedOption) {
        setInputValue(selectedOption.text);
        onChange(selectedOption);
      }
    }
  };

  const handleBlur = (): void => {
    if (inputValue.trim()) {
      const existingOption = options.find(
        (opt) => opt.text.toLowerCase() === inputValue.trim().toLowerCase(),
      );
      if (existingOption) {
        setInputValue(existingOption.text);
        onChange(existingOption);
      } else {
        const newOption = { key: getId(), text: inputValue.trim() };
        setOptions((prevOptions) => [...prevOptions, newOption]);
        setInputValue(newOption.text);
        onChange(newOption);
      }
    }
  };

  return (
    <FluentProvider theme={webLightTheme}>
      <LoadingErrorMessage loading={loading} error={error} />
      {loading && <Spinner aria-label="Loading" />}
      {!loading && !error && (
        <Field label={label} required={required} validationMessage={errorMessage}>
          <Combobox
            value={inputValue}
            onInput={handleInputChange}
            onOptionSelect={handleOptionSelect}
            onBlur={handleBlur}
            freeform
            disabled={disabled}
          >
            {options.map((option) => (
              <Option key={option.key} value={option.key.toString()} text={option.text}>
                {option.text}
              </Option>
            ))}
          </Combobox>
        </Field>
      )}
    </FluentProvider>
  );
};

export default ReportingGroupSelector;
